import { FileRejection, Accept } from '@any-ui-react/dropzone';
import { t } from 'i18next';
import isURL from 'validator/lib/isURL';

import { toast } from '@anyx/shared-ui';


import {
  DimensionsType,
  UploadedDocumentFile,
  UploadedImageFile,
  UploadedVideoFile,
} from './AnyChatMediaUpload';

export const isIdenticalSize = (size1?: DimensionsType, size2?: DimensionsType) => {
  if (!size1 || !size2) return false;

  const sameWidth = size1?.width === size2?.width;
  const sameHeight = size1?.height === size2?.height;

  return sameWidth && sameHeight;
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const formatMimeType = (mimeType: string | string[]) =>
  typeof mimeType === 'string'
    ? mimeType.toUpperCase()
    : mimeType.map((type) => (type.split('/')[1] || '').toUpperCase()).join(', ');

export const formatDropzoneAccept = (accept: Accept): string => {
  return Object.values(accept)
    .reduce((supportedMimeType, acceptMime) => {
      return [...supportedMimeType, ...acceptMime];
    }, [])
    .join(', ');
};

export const isImageFile = (file: DataTransferItem | Blob) => file.type.startsWith('image');

export const isVideoFile = (file: DataTransferItem | Blob) => file.type.startsWith('video');

export const filesRejectHandler = (fileRejections: FileRejection[]) => {
  fileRejections.forEach((fileRejection) => {
    const fileName = fileRejection.file.name;

    const errorMsg: string[] = [];

    fileRejection.errors.forEach((error) => {
      errorMsg.push(
        t(error.code, {
          defaultValue: error.message,
        })
      );
    });
    toast.error(
      <>
        {fileName}

        {errorMsg.map((msg) => (
          <>
            <br /> &bull;&nbsp;{msg}
          </>
        ))}
      </>
    );
  });
};

export const getFileNameFromUrl = (fileUrl = '') => {
  return (isURL(fileUrl) && new URL(fileUrl).pathname.split('/').pop()) || '';
};

export const toUploadImageFile = (imageUrl: string): UploadedImageFile => {
  return {
    type: 'image',
    src: imageUrl,
    name: getFileNameFromUrl(imageUrl),
    state: 'remote',
  };
};

export const getVideoDuration = async (file: File): Promise<number> => {
  const video = document.createElement('video');
  video.preload = 'metadata';

  const duration = await new Promise<number>((resolve, reject) => {
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video metadata'));
    };

    video.src = URL.createObjectURL(file);
  });

  return duration;
};

export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const toUploadVideoFile = (videoUrl: string): UploadedVideoFile => {
  return {
    type: 'video',
    src: videoUrl,
    name: getFileNameFromUrl(videoUrl),
    state: 'remote',
  };
};

export const toUploadDocumentFile = (documentUrl: string): UploadedDocumentFile => {
  return {
    type: 'document',
    src: documentUrl,
    name: getFileNameFromUrl(documentUrl),
    state: 'remote',
  };
};
